<template>
  <div class="data-table-container">
    <table class="data-table-component">
      <thead>
        <tr>
          <th v-for="header in headers" :key="header.fieldName" :class="header.classes" @click="sortTable(header.fieldName)">
            <span class="th-title" :class="{ 'has-sort': sortDirection !== null }">
              {{ trans(header.title) }}
              <span
                v-if="sortFieldName === header.fieldName"
                :class="sortDirection ? 'sort-asc' : 'sort-desc'"
              />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item) in sortedOffers" :key="item.id">
          <td v-for="header in headers" :key="header.fieldName" :class="header.classes">
            <div v-if="header.fieldName === 'actions'">
              <Dropdown v-if="item.state !== officeStateEnum.BLOCKED" :options="dropdownOptions(item)" />
            </div>
            <div v-else>
              <span
                v-if="!header.editable && header.fieldName !== 'name'"
              >
                {{ getFormattedField(item, header) }}
              </span>
              <span
                v-if="!header.editable && header.fieldName === 'name'"
              >
                <a :href="getSellerOfferUrl(item)" class="offer-link" target="_blank">
                  {{ getFormattedField(item, header) }}
                </a>
              </span>
              <div
                v-if="header.fieldName === 'price'"
                class="input-suffix"
                :data-suffix="trans('global.currency_symbol')"
                :class="{ 'disabled': item.state === officeStateEnum.BLOCKED }"
              >
                <CurrencyInput
                  :ref="`price-${item.hash_id}`"
                  :model-value="getField(item, header.fieldName)"
                  :options="{ currency: 'EUR', currencyDisplay: 'hidden' }"
                  class="editable-input"
                  :class="{ 'input-invalid': invalidPrice(getField(item, header.fieldName)) }"
                  :disabled="item.state === officeStateEnum.BLOCKED"
                  @change="updateField(item, header.fieldName, $event)"
                />
                <span
                  v-if="invalidPrice(getField(item, header.fieldName))"
                  class="input-invalid-text"
                >
                  {{ trans('offer.price.max_amount_exceeded') }}
                </span>
              </div>
              <div
                :ref="`container-datepicker-${item.hash_id}`"
                :class="[ 'container-datepicker', {
                  'hide-datepicker': [ '-', trans('offer.availability_now') ].includes(getTextDatePicker(item)) && datepickerDisplayed !== item.hash_id,
                  'hide-btn': !getTextDatePicker(item) && datepickerDisplayed !== item.hash_id
                } ]"
              >
                <button
                  v-if="header.fieldName === 'availability_date' && datepickerDisplayed !== item.hash_id"
                  class="datepicker"
                  @click="openDatePicker(item.hash_id)"
                >
                  {{ getTextDatePicker(item) }}
                </button>
                <VueDatePicker
                  v-if="header.fieldName === 'availability_date'"
                  :ref="`datepicker-${item.hash_id}`"
                  name="availability_date"
                  :model-value="getField(item, header.fieldName)"
                  :disabled="item.state === officeStateEnum.BLOCKED"
                  class="datepicker"
                  :format="displayFormat"
                  locale="fr"
                  :enable-time-picker="false"
                  :clearable="false"
                  auto-apply
                  :alt-position="getDatePickerPosition"
                  @closed="datepickerDisplayed = null"
                  @update:model-value="updateField(item, header.fieldName, getSimpleDateIso($event))"
                  @openMenu="closeModal(item.hash_id)"
                >
                  <template #input-icon>
                    <span class="datepicker__icon datepicker__icon--datepicker" />
                  </template>
                </VueDatePicker>
              </div>
              <SelectOfferStates
                v-if="header.fieldName === 'state' && !isStateIsDisabled(item)"
                :offer="item"
                @change="selectState"
              />
              <select
                v-if="header.fieldName === 'state' && isStateIsDisabled(item)"
                disabled
              >
                <option>{{ getField(item, header.fieldName) }}</option>
              </select>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="sortedOffers.length !== 0" class="data-table-component-total">
      {{ transchoice('offer.total', { count: total, offers: sortedOffers.length }) }}
    </p>
    <div class="data-table-component-footer">
      <div class="data-table-component-footer__sticky">
        <div class="sticky-buttons">
          <button
            class="btn--invisible"
            @click="cancelEdit"
          >
            <span>{{ trans('global.cancel') }}</span>
          </button>
          <button
            class="btn--primary"
            :class="{ 'loading-btn': isLoading && !isLoadingNext }"
            :disabled="!canSave || isLoading"
            @click="saveOffers"
          >
            <span>{{ trans('offer.save_all') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { saveOffers } from '@api/offers/apiOffers';
import { addressToString } from '@elements/address-utils';
import { inputNumberRemoveSpecialChar } from '@elements/number-formatter';
import { trans, transchoice } from '@elements/translation';
import { getSellerOfferUrl, HEADER_TABLE_LIST } from '@organisms/offer/offer-utils';
import { useOfferListStore } from '@stores/useOfferListStore';
import VueDatePicker from '@vuepic/vue-datepicker';
import CurrencyInput from '@components/CurrencyInput.vue';
import Dropdown from '@components-molecules/Dropdown.vue';
import OFFICE_STATE from '@organisms/offer/office-state-enum';
import eventBus from '@tools/event-bus';
import { storeToRefs } from 'pinia';
import moment from 'moment';
import SelectOfferStates from './SelectOfferStates.vue';
import 'moment/dist/locale/fr';

moment.locale('fr');

export default {
  components: { SelectOfferStates, CurrencyInput, Dropdown, VueDatePicker },
  setup() {
    const store = useOfferListStore();
    const { load: loadOffers } = store;
    const { isLoading, isLoadingNext, offers, total } = storeToRefs(store);
    return {
      isLoading,
      isLoadingNext,
      loadOffers,
      headers: HEADER_TABLE_LIST,
      offers,
      total,
      officeStateEnum: OFFICE_STATE,
    };
  },
  data() {
    return {
      offersToSave: {},
      sortDirection: false,
      sortFieldName: null,
      displayFormat: 'dd/MM/yyyy',
      datepickerDisplayed: null,
      priceErrors: false,
      canSave: false,
    };
  },
  computed: {
    sortedOffers() {
      if (this.sortFieldName) {
        return [ ...this.offers ].sort((a, b) => {
          const valueA = this.getField(a, this.sortFieldName);
          const valueB = this.getField(b, this.sortFieldName);
          return this.sortDirection ? this.compare(valueA, valueB) : this.compare(valueB, valueA);
        });
      }
      return [ ...this.offers ];
    },
  },
  methods: {
    transchoice,
    trans,
    getSellerOfferUrl,
    getDatePickerPosition() {
      return { top: 48, left: 0 };
    },
    setCanSave() {
      this.canSave = !this.priceErrors && Object.keys(this.offersToSave).length > 0;
    },
    openDatePicker(hash) {
      this.datepickerDisplayed = hash;
      this.$refs[`container-datepicker-${hash}`][0].classList.remove('hide-datepicker');
      this.$refs[`datepicker-${hash}`][0].openMenu();
    },
    getTextDatePicker(item) {
      if (item.state === OFFICE_STATE.UNPUBLISHED) {
        return '-';
      }
      if (item.availability_date && new Date(item.availability_date.iso) < new Date()) {
        return trans('offer.availability_now');
      }
      return false;
    },
    invalidPrice(price) {
      return parseInt(price, 10) > 99999999;
    },
    closeModal(datepicker) {
      eventBus.dispatchEvent(new CustomEvent('offer-close-modale', { detail: datepicker }));
    },
    dropdownOptions(offer) {
      return [
        {
          hidden: true,
          text: trans('offer.action.duplicate'),
          href: offer.duplicate_url ? offer.duplicate_url : '',
          target: '_blank',
        },
        {
          hidden: true,
          text: trans('offer.action.edit'),
          href: offer.edit_url ? offer.edit_url : '',
          target: '_blank',
        },
      ];
    },
    isStateIsDisabled(offer) {
      return [
        OFFICE_STATE.OFFMARKET,
        OFFICE_STATE.DISABLED,
        OFFICE_STATE.DRAFT,
        OFFICE_STATE.MODERATION,
        OFFICE_STATE.BLOCKED,
        ...OFFICE_STATE.DRAFT_LOOK_ALIKE,
      ].includes(offer.state);
    },
    getSimpleDateIso(date) {
      const momentDate = moment(date);
      return {
        iso: momentDate.toISOString(),
        unix: momentDate.unix(),
      };
    },
    compare(valueA, valueB) {
      if ((typeof valueA === 'number' && typeof valueB === 'number') || this.sortFieldName === 'price') {
        const aCleaned = valueA.toString().replace(/\s/g, '').replace('€', '');
        const bCleaned = valueB.toString().replace(/\s/g, '').replace('€', '');
        return aCleaned - bCleaned;
      }
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const dateFormat = /^\d{2}\/\d{2}\/\d{4}$/;
        if (dateFormat.test(valueA) && dateFormat.test(valueB)) {
          const dateA = new Date(valueA.split('/').reverse().join('/'));
          const dateB = new Date(valueB.split('/').reverse().join('/'));
          return dateA - dateB;
        }
        return valueA.localeCompare(valueB);
      }
      return typeof valueA === 'number' ? -1 : 1;
    },
    selectState(data) {
      this.save({ ...data.offer, state: data.state }, 'state');
    },
    save(offer, field) {
      const updatedOffer = {};
      switch (field) {
      case 'price':
        if (updatedOffer.price === undefined) {
          updatedOffer.price = 0;
        }
        updatedOffer.price = inputNumberRemoveSpecialChar(offer.price);
        break;
      case 'availability_date':
        updatedOffer.availability_date = moment(offer.availability_date.iso).format('YYYY-MM-DD');
        break;
      default:
        updatedOffer[field] = offer[field];
      }
      this.offersToSave[offer.hash_id] = updatedOffer;
      this.checkPriceErrors();
      this.setCanSave();
    },
    updateField(item, fieldName, value) {
      if (item[fieldName] === value) {
        return;
      }

      const fields = fieldName.split('.');
      const lastField = fields.pop();
      const obj = fields.reduce((val, key) => val[key], item);
      obj[lastField] = value;
      this.save(item, fieldName);
    },
    getFormattedField(item, headers) {
      // eslint-disable-next-line no-prototype-builtins
      if (headers.hasOwnProperty('formatted')) {
        return headers.formatted(item);
      }
      if (headers.fieldName !== 'state') {
        const fields = headers.fieldName.split('.');
        return fields.reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : ''), item);
      }
      const key = OFFICE_STATE.DRAFT_LOOK_ALIKE.includes(item[headers.fieldName])
        ? OFFICE_STATE.DRAFT
        : item[headers.fieldName];
      return transchoice(`offer.state.${key}`, { count: 1 });
    },
    getField(item, fieldName) {
      switch (fieldName) {
      case 'state':
        // eslint-disable-next-line no-case-declarations
        const state = OFFICE_STATE.DRAFT_LOOK_ALIKE.includes(item[fieldName])
          ? OFFICE_STATE.DRAFT
          : item[fieldName];
        return transchoice(`offer.state.${state}`, { count: 1 });
      case 'availability_date':
        return item.availability_date ? item.availability_date.iso : '';
      case 'price':
        return item.price;
      case 'address.name':
        if (!item.address) {
          return '';
        }
        if (item.address.name === '') {
          return addressToString(item.address);
        }
        return item.address.name;
      default:
        // eslint-disable-next-line no-case-declarations
        const fields = fieldName.split('.');
        return fields.reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : ''), item);
      }
    },
    sortTable(fieldName) {
      if (fieldName === 'actions') {
        return;
      }
      if (this.sortFieldName === fieldName) {
        this.sortDirection = !this.sortDirection;
        return;
      }
      this.sortFieldName = fieldName;
      this.sortDirection = true;
    },
    checkPriceErrors() {
      this.priceErrors = false;
      Object.keys(this.offersToSave).forEach((hashId) => {
        if (
          this.offersToSave[hashId].price
          && parseInt(this.offersToSave[hashId].price.toString().replace(/\s/g, ''), 10) > 99999999
        ) {
          this.priceErrors = true;
        }
      });
      this.setCanSave();
    },
    async saveOffers() {
      this.isLoading = true;
      if (await saveOffers(this.offersToSave)) {
        await this.loadOffers();
        this.offersToSave = {};
        this.setCanSave();
      }
      this.isLoading = false;
    },
    cancelEdit() {
      this.offersToSave = {};
      this.offers = [];
      this.canSave = false;
      this.loadOffers();
    },
  },
};
</script>
